'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getConsentPlugin } from './getConsentPlugin';
import { ConsentPlugin, ConsentCategory } from './types';

let activeConsentPlugin: ConsentPlugin | null = null;

export const getActiveConsentPlugin = (): ConsentPlugin | null =>
  activeConsentPlugin;

const ConsentContext = createContext<{
  hasConsent: (category: ConsentCategory) => boolean;
  setCookiesLanguage: (languageCode: string) => void;
  consentVersion: number;
} | null>(null);

export const ConsentProvider = ({
  provider = 'noop',
  locale,
  clientId,
  cookiesVersion,
  children,
  tenantId,
}: {
  provider: string;
  locale: string;
  clientId: string;
  cookiesVersion: string;
  children: React.ReactNode;
  tenantId: string;
}) => {
  const [consentVersion, setConsentVersion] = useState(0);
  const pluginRef = useRef<ConsentPlugin | null>(null);

  useEffect(() => {
    const resolved = getConsentPlugin(provider, {
      clientId,
      cookiesVersion,
      locale,
      tenantId,
    });
    pluginRef.current = resolved;
    activeConsentPlugin = resolved;
    if ('onChange' in resolved && typeof resolved.onChange === 'function') {
      resolved.onChange(() => setConsentVersion(v => v + 1));
    }
    Promise.resolve(resolved.init()).then(() => {
      setConsentVersion(v => v + 1);
    });
  }, [provider, locale, clientId, cookiesVersion, tenantId]);

  const hasConsent = useCallback(
    (category: ConsentCategory) =>
      pluginRef.current?.hasConsent(category) ?? false,
    [],
  );

  const setCookiesLanguage = useCallback(
    (languageCode: string) =>
      pluginRef.current?.setCookiesLanguage(languageCode),
    [],
  );

  return (
    <ConsentContext.Provider
      value={{ hasConsent, setCookiesLanguage, consentVersion }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};
